import * as React from "react";
import {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
} from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

interface SidebarContextValue {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
}

// Create the context internally
const SidebarContext = createContext<SidebarContextValue>({
  state: "expanded",
  open: true,
  setOpen: () => {},
  isMobile: false,
  openMobile: false,
  setOpenMobile: () => {},
  toggleSidebar: () => {},
});

// Create a named function component for the provider
function SidebarProviderComponent({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(true);
  const [openMobile, setOpenMobile] = React.useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  
  const state: "expanded" | "collapsed" = open ? "expanded" : "collapsed";
  
  const toggleSidebar = React.useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const value = React.useMemo<SidebarContextValue>(() => ({
    state,
    open,
    setOpen,
    isMobile,
    openMobile,
    setOpenMobile,
    toggleSidebar,
  }), [state, open, isMobile, openMobile, toggleSidebar]);

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

// Export the memoized provider component
export const SidebarProvider = React.memo(SidebarProviderComponent);

// Export the hook
export function useSidebar(): SidebarContextValue {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}

function Sidebar({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) {
  const { isMobile, openMobile, setOpenMobile } = useSidebar();

  return (
    <>
      {isMobile ? (
        <Sheet open={openMobile} onOpenChange={setOpenMobile}>
          <SheetContent
            side="left"
            className="w-full sm:max-w-xs border-r border-border p-0"
          >
            {children}
          </SheetContent>
        </Sheet>
      ) : (
        <aside className={cn("flex flex-col shrink-0 border-r border-border", className)} {...props}>
          {children}
        </aside>
      )}
    </>
  );
}

function SidebarHeader({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex flex-row items-center justify-between h-16 px-4 border-b border-border", className)} {...props}>
      {children}
    </div>
  );
}

function SidebarContent({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex flex-col flex-1 min-h-0 py-2", className)} {...props}>
      {children}
    </div>
  );
}

function SidebarMenu({ className, children, ...props }: React.HTMLAttributes<HTMLUListElement>) {
  return (
    <ul className={cn("flex flex-col gap-1", className)} {...props}>
      {children}
    </ul>
  );
}

function SidebarMenuItem({ className, children, ...props }: React.HTMLAttributes<HTMLLIElement>) {
  return (
    <li className={cn("", className)} {...props}>
      {children}
    </li>
  );
}

interface SidebarMenuButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
  tooltip?: string;
}

function SidebarMenuButton({ isActive, tooltip, className, children, ...props }: SidebarMenuButtonProps) {
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const { state } = useSidebar();

  React.useEffect(() => {
    if (state === "expanded") {
      setIsTooltipOpen(false);
    }
  }, [state]);

  return (
    <div className="relative">
      <button
        className={cn(
          "group flex items-center gap-3 px-3 py-2 rounded-md w-full text-sm font-medium transition-colors hover:bg-secondary/50",
          isActive && "bg-secondary/50 text-primary",
          className
        )}
        {...props}
      >
        {children}
      </button>
      {tooltip && state === "collapsed" && (
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-3 bg-gray-800 text-white text-xs px-2 py-1 rounded-md z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          {tooltip}
        </span>
      )}
    </div>
  );
}

function SidebarTrigger({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 px-2 py-1",
        className
      )}
      onClick={toggleSidebar}
      {...props}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
      >
        <path d="M3 9h18M3 15h18M3 20h18M3 4h18" />
      </svg>
      <span className="sr-only">Toggle Sidebar</span>
    </button>
  );
}

function SidebarInset({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("px-4 py-2", className)} {...props}>
      {children}
    </div>
  );
}

function SidebarGroup({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex flex-col gap-1", className)} {...props}>
      {children}
    </div>
  );
}

function SidebarGroupLabel({ className, children, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={cn("px-3 py-2 text-xs font-semibold text-muted-foreground uppercase", className)} {...props}>
      {children}
    </span>
  );
}

// Export all components as named exports
export {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
};
